import { useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { LicenceClass, LicenceClassCode, Page, RequirementSpec, RequirementType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const CLASS_CODES: LicenceClassCode[] = [
  'TOURISM_ACCOM', 'RESTAURANT', 'LIQUOR_ON', 'LIQUOR_OFF', 'RETAIL', 'ROAD_TRANSPORT', 'GAMBLING',
  'PROFESSIONAL_ENGINEERING', 'PROFESSIONAL_ACCOUNTING', 'PROFESSIONAL_LEGAL', 'PROFESSIONAL_MEDICAL',
];
const REQ_TYPES: RequirementType[] = ['SEYX_CHECK', 'DOC_UPLOAD', 'INSPECTION', 'COMMITTEE'];

/** Public licence-class catalogue — no sign-in required. Shows exactly what an applicant must clear. */
export function LicenceClasses() {
  const { persona } = useAuth();
  const canManage = hasAnyRole(persona?.roles, M14_ROLES.LICENSING_OFFICER, M14_ROLES.SENIOR_LO);
  const [showForm, setShowForm] = useState(false);
  const classesQ = useApiQuery<Page<LicenceClass>>('/api/v1/m14/classes?limit=100');

  return (
    <div>
      <PageHeader
        title="Licence Classes"
        subtitle="The configurable SLA licence engine — each class carries its own requirement checklist."
        actions={canManage ? <button className="btn-primary" onClick={() => setShowForm((s) => !s)}>+ Add / update class</button> : undefined}
      />
      {showForm ? <ClassForm onDone={() => { setShowForm(false); classesQ.reload(); }} /> : null}
      {classesQ.error ? <ErrorNote message={classesQ.error} /> : null}
      {classesQ.loading ? (
        <Spinner />
      ) : (classesQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No licence classes configured" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {classesQ.data!.items.map((c) => (
            <div key={c.id} className="card p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold">{c.name}</div>
                  <div className="text-xs text-slate-400">{c.code}</div>
                </div>
                <div className="flex items-center gap-1.5">
                  {c.requiresBoard ? <StatusBadge status="BOARD_AGENDA" /> : null}
                  <StatusBadge status={c.active ? 'ACTIVE' : 'CLOSED'} />
                </div>
              </div>
              {c.description ? <p className="mt-2 text-xs text-slate-600">{c.description}</p> : null}
              <div className="mt-2 text-xs text-slate-500">Validity: {c.validityMonths} months · Renewal window: {c.renewalWindowDays} days · Fee code: {c.feeCode}</div>
              <div className="mt-3 border-t border-slate-100 pt-2">
                <div className="mb-1 text-[11px] font-semibold uppercase text-slate-400">Requirement checklist</div>
                <ul className="space-y-1">
                  {c.requirements.map((r, i) => (
                    <li key={i} className="flex items-center justify-between text-xs">
                      <span>{r.label}</span>
                      <StatusBadge status={r.type} />
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClassForm({ onDone }: { onDone: () => void }) {
  const [code, setCode] = useState<LicenceClassCode>('TOURISM_ACCOM');
  const [name, setName] = useState('');
  const [feeCode, setFeeCode] = useState('');
  const [validityMonths, setValidityMonths] = useState('12');
  const [renewalWindowDays, setRenewalWindowDays] = useState('60');
  const [requirements, setRequirements] = useState<RequirementSpec[]>([{ code: 'TAX_CLEARANCE', type: 'SEYX_CHECK', label: 'Tax clearance check' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateReq = (i: number, field: keyof RequirementSpec, value: string) => {
    setRequirements((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m14/classes', {
        code,
        name,
        feeCode,
        validityMonths: Number.parseInt(validityMonths, 10),
        renewalWindowDays: Number.parseInt(renewalWindowDays, 10),
        requirements,
        active: true,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not save the class');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card mb-5 p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Class code">
          <select className="field-input" value={code} onChange={(e) => setCode(e.target.value as LicenceClassCode)}>
            {CLASS_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Name"><input className="field-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Fee code"><input className="field-input" value={feeCode} onChange={(e) => setFeeCode(e.target.value)} placeholder="SLA_TOURISM_FEE" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Validity (months)"><input className="field-input" type="number" value={validityMonths} onChange={(e) => setValidityMonths(e.target.value)} /></Field>
          <Field label="Renewal window (days)"><input className="field-input" type="number" value={renewalWindowDays} onChange={(e) => setRenewalWindowDays(e.target.value)} /></Field>
        </div>
      </div>

      <div className="mt-3 border-t border-slate-100 pt-3">
        <div className="mb-2 text-xs font-semibold uppercase text-slate-500">Requirement checklist</div>
        <div className="space-y-2">
          {requirements.map((r, i) => (
            <div key={i} className="grid grid-cols-5 gap-2">
              <input className="field-input col-span-1" placeholder="code" value={r.code} onChange={(e) => updateReq(i, 'code', e.target.value)} />
              <select className="field-input col-span-1" value={r.type} onChange={(e) => updateReq(i, 'type', e.target.value)}>
                {REQ_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <input className="field-input col-span-2" placeholder="label" value={r.label} onChange={(e) => updateReq(i, 'label', e.target.value)} />
              <button className="btn-secondary" onClick={() => setRequirements((rs) => rs.filter((_, idx) => idx !== i))}>Remove</button>
            </div>
          ))}
        </div>
        <button className="btn-secondary mt-2 !py-1 text-xs" onClick={() => setRequirements((rs) => [...rs, { code: '', type: 'SEYX_CHECK', label: '' }])}>+ Add requirement</button>
      </div>

      <button className="btn-primary mt-3" onClick={submit} disabled={busy || !name || !feeCode || requirements.some((r) => !r.code || !r.label)}>
        {busy ? 'Saving…' : 'Save licence class'}
      </button>
    </div>
  );
}
